import React, { useState, memo, FormEvent } from "react";
import FixtureState from "../fixtureState";
import ManageFixture from "../manageFixture";
import useWindowDimensions from "../../utils/hooks/useWindowDimensions";
import { FixtureStatus as FixtureStatusType } from "../../utils/hooks/useFixtures";

import styles from "./fixture.module.scss";

export interface FixtureProps {
    id: string;
    result: string;
    homeTeam: string;
    awayTeam: string;
    competition: string;
    state: FixtureStatusType;
    date: string;
    shortDate: string;
    competitionId: string;
    onClick: (id: string) => void;
    onDropdownSelection: (id: string, selection: string) => void;
}

const MobileFixture = memo<FixtureProps>(
    ({ id, result, homeTeam, awayTeam, competition, date, shortDate, state, onClick }) => {
        const handleClick = (event: FormEvent<EventTarget>) => {
            event.preventDefault();
            onClick(id);
        };

        return (
            <a href={`/fixtures/${id}`} className={styles.fixtureContainer} onClick={handleClick}>
                <div className={styles.date} title={date}>
                    <div>{shortDate}</div>
                </div>
                <div className={styles.fixture}>
                    <div className={styles.team}>{homeTeam}</div>
                    <div className={styles.result}>{result}</div>
                    <div className={styles.team}>{awayTeam}</div>
                </div>
                <div className={styles.competition}>{competition}</div>
                <div className={styles.fixtureStateContainer}>
                    <FixtureState state={state} classes={styles.fixtureState} />
                </div>
            </a>
        );
    }
);

const BrowserFixture = memo<FixtureProps>(
    ({
        id,
        result,
        homeTeam,
        awayTeam,
        competition,
        date,
        shortDate,
        state,
        competitionId,
        onClick,
        onDropdownSelection
    }) => {
        const [active, setActive] = useState(false);

        const fixtureContainerClasses = `${styles.fixtureContainer} ${active ? styles.active : ""}`;

        const handleOpen = () => setActive(true);

        const handleClose = () => setActive(false);

        const handleClick = (event: FormEvent<EventTarget>) => {
            event.preventDefault();
            onClick(id);
        };

        const handleSelect = (selection: string) => {
            onDropdownSelection(id, selection);
        };

        return (
            <div className={fixtureContainerClasses}>
                <a href={`/fixtures/${id}`} className={styles.childContainer} onClick={handleClick}>
                    <div className={styles.date} title={date}>
                        <div>{shortDate}</div>
                        <div className={styles.competition}>{competition}</div>
                    </div>
                    <div className={styles.fixture}>
                        <div className={styles.team}>{homeTeam}</div>
                        <div className={styles.result}>{result}</div>
                        <div className={styles.team}>{awayTeam}</div>
                    </div>
                    <div className={styles.fixtureStateContainer}>
                        <FixtureState state={state} classes={styles.fixtureState} />
                    </div>
                </a>
                <ManageFixture
                    competitionId={competitionId}
                    homeTeam={homeTeam}
                    awayTeam={awayTeam}
                    state={state}
                    classes={styles.dropdown}
                    onSelect={handleSelect}
                    onOpen={handleOpen}
                    onClose={handleClose}
                />
            </div>
        );
    }
);

const Fixture = memo<FixtureProps>(({ ...props }) => {
    const { width } = useWindowDimensions();

    return width >= 992 ? <BrowserFixture {...props} /> : <MobileFixture {...props} />;
});

export default Fixture;
